import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }; }

const DEFAULTS = {
  name:'', description:'', osTarget:'windows',
  windowsUpdate:{ enabled:true, autoDownload:true, autoInstall:false, activeHoursStart:8, activeHoursEnd:18, wsusServer:'', deferFeatureUpdatesDays:7, deferQualityUpdatesDays:0 },
  bitlocker:{ enforceEncryption:false, encryptionMethod:'AES256', requireRecoveryKey:true, allowSuspension:false },
  firewall:{ enforceFirewall:true, domainProfile:'on', privateProfile:'on', publicProfile:'on' },
  passwordPolicy:{ enforce:false, minLength:12, complexity:true, maxAgeDays:90, lockoutThreshold:5, lockoutDurationMinutes:15 },
  screenLock:{ enforce:true, timeoutMinutes:10, requirePassword:true },
  customScript:'', applyInterval:'daily',
};

export default function DeviceProfiles({ user }) {
  const [profiles,  setProfiles]  = useState([]);
  const [devices,   setDevices]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState({ text:'', type:'' });
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(DEFAULTS);
  const [assignModal, setAssignModal] = useState(null);
  const [selDevs,   setSelDevs]   = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [activeTab, setActiveTab] = useState('windows-update');

  function flash(t,type='success') { setMsg({text:t,type}); setTimeout(()=>setMsg({text:'',type:''}),4000); }

  const load = useCallback(async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        axios.get('/api/profiles', auth()),
        axios.get('/api/agents', auth()),
      ]);
      setProfiles(pRes.data);
      setDevices(dRes.data.filter?dRes.data.filter(d=>!d.archivedAt):dRes.data);
    } catch(e) { flash(e.response?.data?.error||'Failed to load','error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(()=>{ load(); },[load]);

  function openCreate() { setEditing(null); setForm(DEFAULTS); setActiveTab('windows-update'); setShowForm(true); }
  function openEdit(p)  { setEditing(p.id); setForm({...DEFAULTS,...p}); setActiveTab('windows-update'); setShowForm(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await axios.put(`/api/profiles/${editing}`, form, auth()); flash('Profile updated'); }
      else         { await axios.post('/api/profiles', form, auth()); flash('Profile created'); }
      setShowForm(false); load();
    } catch(e) { flash(e.response?.data?.error||'Save failed','error'); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm('Delete this profile? All device assignments will also be removed.')) return;
    try { await axios.delete(`/api/profiles/${id}`, auth()); flash('Deleted'); load(); }
    catch(e) { flash('Failed to delete','error'); }
  }

  async function assign() {
    if (!selDevs.length) return flash('Select devices','error');
    setAssigning(true);
    try {
      const r = await axios.post(`/api/profiles/${assignModal.id}/assign`, { deviceIds: selDevs }, auth());
      flash(`Profile assigned to ${r.data.assigned} device(s)`);
      setAssignModal(null); setSelDevs([]);
    } catch(e) { flash(e.response?.data?.error||'Assign failed','error'); }
    finally { setAssigning(false); }
  }

  function setWu(k,v)  { setForm(f=>({...f,windowsUpdate:{...f.windowsUpdate,[k]:v}})); }
  function setBl(k,v)  { setForm(f=>({...f,bitlocker:{...f.bitlocker,[k]:v}})); }
  function setFw(k,v)  { setForm(f=>({...f,firewall:{...f.firewall,[k]:v}})); }
  function setPp(k,v)  { setForm(f=>({...f,passwordPolicy:{...f.passwordPolicy,[k]:v}})); }
  function setSl(k,v)  { setForm(f=>({...f,screenLock:{...f.screenLock,[k]:v}})); }

  const TABS = [
    {id:'windows-update',label:'Windows Update',icon:'⬆'},
    {id:'bitlocker',     label:'BitLocker',      icon:'🔐'},
    {id:'firewall',      label:'Firewall',        icon:'🛡'},
    {id:'password',      label:'Password Policy', icon:'🔑'},
    {id:'screen-lock',   label:'Screen Lock',     icon:'🔒'},
    {id:'script',        label:'Custom Script',   icon:'📜'},
  ];

  return (
    <div style={{padding:'24px'}}>
      {msg.text && <div style={{padding:'10px 16px',borderRadius:8,marginBottom:16,background:msg.type==='error'?'#fee2e2':'#dcfce7',color:msg.type==='error'?'#dc2626':'#16a34a',fontWeight:600}}>{msg.type==='error'?'✘':'✔'} {msg.text}</div>}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
        <div>
          <h2 style={{margin:0,fontSize:20,fontWeight:700,color:'#1e293b'}}>Device Profiles</h2>
          <p style={{margin:'4px 0 0',color:'#64748b',fontSize:14}}>Policy templates applied to endpoints — Windows Update, BitLocker, firewall, passwords</p>
        </div>
        <button onClick={openCreate} style={{background:'#1e40af',color:'#fff',border:'none',borderRadius:8,padding:'10px 20px',fontWeight:600,cursor:'pointer',fontSize:14}}>+ New Profile</button>
      </div>

      {loading ? <div style={{textAlign:'center',padding:40,color:'#94a3b8'}}>Loading...</div> :
      profiles.length===0 ? (
        <div style={{textAlign:'center',padding:60,color:'#94a3b8'}}><div style={{fontSize:40,marginBottom:12}}>⚙️</div><div style={{fontWeight:600}}>No profiles yet</div><div style={{fontSize:14,marginTop:8}}>Create a policy profile and assign it to your endpoints</div></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))',gap:16}}>
          {profiles.map(p=>(
            <div key={p.id} style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15,color:'#1e293b'}}>{p.name}</div>
                  {p.description && <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{p.description}</div>}
                </div>
                <span style={{background:'#dbeafe',color:'#1d4ed8',borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700}}>{p.osTarget}</span>
              </div>
              {/* Policy badges */}
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
                {p.windowsUpdate?.enabled!==false && <span style={{background:'#ede9fe',color:'#7c3aed',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>⬆ WU</span>}
                {p.bitlocker?.enforceEncryption && <span style={{background:'#fee2e2',color:'#dc2626',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>🔐 BitLocker</span>}
                {p.firewall?.enforceFirewall && <span style={{background:'#dcfce7',color:'#16a34a',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>🛡 FW</span>}
                {p.passwordPolicy?.enforce && <span style={{background:'#fef3c7',color:'#d97706',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>🔑 Passwd</span>}
                {p.screenLock?.enforce && <span style={{background:'#f0f9ff',color:'#0369a1',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>🔒 Lock</span>}
                {p.customScript && <span style={{background:'#f1f5f9',color:'#475569',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>📜 Script</span>}
                <span style={{background:'#f1f5f9',color:'#94a3b8',borderRadius:5,padding:'2px 7px',fontSize:10}}>↻ {p.applyInterval}</span>
              </div>
              <div style={{fontSize:11,color:'#94a3b8',marginBottom:14}}>
                {p.deviceCount||0} device(s) assigned · Created by {p.createdBy||'admin'}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>{setAssignModal(p);setSelDevs([]);}} style={{flex:1,background:'#1e40af',color:'#fff',border:'none',borderRadius:7,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer'}}>👥 Assign</button>
                <button onClick={()=>openEdit(p)} style={{flex:1,background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer',color:'#475569'}}>✏ Edit</button>
                <button onClick={()=>del(p.id)} style={{background:'#fee2e2',border:'none',borderRadius:7,padding:'8px 10px',fontSize:12,cursor:'pointer',color:'#dc2626'}}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile edit/create modal */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,width:620,maxWidth:'96vw',maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #f1f5f9'}}>
              <h3 style={{margin:0,fontSize:18,fontWeight:700}}>{editing?'Edit':'New'} Profile</h3>
            </div>
            <form onSubmit={save} style={{flex:1,overflow:'auto',padding:24}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4}}>Profile Name*</label>
                  <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required placeholder="e.g. Standard Workstation Policy" style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4}}>Description</label>
                  <input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Optional" style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
                </div>
                <div>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4}}>OS Target</label>
                  <select value={form.osTarget} onChange={e=>setForm({...form,osTarget:e.target.value})} style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14}}>
                    <option value="windows">Windows</option><option value="linux">Linux</option><option value="all">All</option>
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4}}>Apply Interval</label>
                  <select value={form.applyInterval} onChange={e=>setForm({...form,applyInterval:e.target.value})} style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14}}>
                    <option value="startup">On Startup</option><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="manual">Manual</option>
                  </select>
                </div>
              </div>

              {/* Policy tabs */}
              <div style={{display:'flex',gap:2,marginBottom:16,borderBottom:'2px solid #f1f5f9',overflowX:'auto'}}>
                {TABS.map(t=>(
                  <button key={t.id} type="button" onClick={()=>setActiveTab(t.id)} style={{padding:'8px 14px',border:'none',borderBottom:activeTab===t.id?'2px solid #1e40af':'2px solid transparent',background:'none',cursor:'pointer',fontWeight:600,fontSize:12,color:activeTab===t.id?'#1e40af':'#64748b',marginBottom:-2,whiteSpace:'nowrap'}}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>

              {activeTab==='windows-update' && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1'}}><input type="checkbox" checked={!!form.windowsUpdate?.enabled} onChange={e=>setWu('enabled',e.target.checked)} /><span style={{fontWeight:600,fontSize:13}}>Enable Windows Update service</span></label>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.windowsUpdate?.autoDownload} onChange={e=>setWu('autoDownload',e.target.checked)} /><span style={{fontSize:13}}>Auto-download updates</span></label>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.windowsUpdate?.autoInstall} onChange={e=>setWu('autoInstall',e.target.checked)} /><span style={{fontSize:13}}>Auto-install updates</span></label>
                  {['activeHoursStart','activeHoursEnd','deferFeatureUpdatesDays','deferQualityUpdatesDays'].map(k=>(
                    <div key={k}><label style={{display:'block',fontSize:12,color:'#374151',marginBottom:3,fontWeight:600}}>{k.replace(/([A-Z])/g,' $1').replace('Days',' Days').trim()}</label>
                    <input type="number" value={form.windowsUpdate?.[k]||0} onChange={e=>setWu(k,parseInt(e.target.value))} style={{width:'100%',padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,boxSizing:'border-box'}} /></div>
                  ))}
                  <div style={{gridColumn:'1/-1'}}><label style={{display:'block',fontSize:12,color:'#374151',marginBottom:3,fontWeight:600}}>WSUS Server URL (blank=Windows Update)</label>
                  <input value={form.windowsUpdate?.wsusServer||''} onChange={e=>setWu('wsusServer',e.target.value)} placeholder="http://wsus.company.local" style={{width:'100%',padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,boxSizing:'border-box'}} /></div>
                </div>
              )}

              {activeTab==='bitlocker' && (
                <div style={{display:'grid',gap:10}}>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.bitlocker?.enforceEncryption} onChange={e=>setBl('enforceEncryption',e.target.checked)} /><span style={{fontWeight:600,fontSize:13}}>Enforce BitLocker encryption on C:</span></label>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.bitlocker?.requireRecoveryKey} onChange={e=>setBl('requireRecoveryKey',e.target.checked)} /><span style={{fontSize:13}}>Require recovery key (backed up to PatchSphere)</span></label>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.bitlocker?.allowSuspension} onChange={e=>setBl('allowSuspension',e.target.checked)} /><span style={{fontSize:13}}>Allow BitLocker suspension</span></label>
                </div>
              )}

              {activeTab==='firewall' && (
                <div style={{display:'grid',gap:10}}>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.firewall?.enforceFirewall} onChange={e=>setFw('enforceFirewall',e.target.checked)} /><span style={{fontWeight:600,fontSize:13}}>Enforce Windows Firewall</span></label>
                  {['domainProfile','privateProfile','publicProfile'].map(k=>(
                    <div key={k} style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{fontSize:13,width:130,color:'#374151',fontWeight:600,textTransform:'capitalize'}}>{k.replace('Profile',' Profile')}</span>
                      <select value={form.firewall?.[k]||'on'} onChange={e=>setFw(k,e.target.value)} style={{padding:'6px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13}}>
                        <option value="on">On</option><option value="off">Off</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {activeTab==='password' && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1'}}><input type="checkbox" checked={!!form.passwordPolicy?.enforce} onChange={e=>setPp('enforce',e.target.checked)} /><span style={{fontWeight:600,fontSize:13}}>Enforce password policy</span></label>
                  {[['minLength','Min Length'],['maxAgeDays','Max Age (days)'],['lockoutThreshold','Lockout Threshold'],['lockoutDurationMinutes','Lockout Duration (min)']].map(([k,l])=>(
                    <div key={k}><label style={{display:'block',fontSize:12,color:'#374151',marginBottom:3,fontWeight:600}}>{l}</label>
                    <input type="number" value={form.passwordPolicy?.[k]||0} onChange={e=>setPp(k,parseInt(e.target.value))} style={{width:'100%',padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,boxSizing:'border-box'}} /></div>
                  ))}
                  <label style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1'}}><input type="checkbox" checked={!!form.passwordPolicy?.complexity} onChange={e=>setPp('complexity',e.target.checked)} /><span style={{fontSize:13}}>Require complexity (upper, lower, number, symbol)</span></label>
                </div>
              )}

              {activeTab==='screen-lock' && (
                <div style={{display:'grid',gap:10}}>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.screenLock?.enforce} onChange={e=>setSl('enforce',e.target.checked)} /><span style={{fontWeight:600,fontSize:13}}>Enforce screen lock timeout</span></label>
                  <div><label style={{display:'block',fontSize:12,color:'#374151',marginBottom:3,fontWeight:600}}>Timeout (minutes)</label>
                  <input type="number" value={form.screenLock?.timeoutMinutes||10} onChange={e=>setSl('timeoutMinutes',parseInt(e.target.value))} min="1" style={{width:120,padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13}} /></div>
                  <label style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" checked={!!form.screenLock?.requirePassword} onChange={e=>setSl('requirePassword',e.target.checked)} /><span style={{fontSize:13}}>Require password on unlock</span></label>
                </div>
              )}

              {activeTab==='script' && (
                <div>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4}}>Custom PowerShell / Bash Script</label>
                  <textarea value={form.customScript||''} onChange={e=>setForm({...form,customScript:e.target.value})} rows={8} placeholder="# PowerShell or bash script to enforce custom policy&#10;# Runs as SYSTEM / root&#10;# e.g: Set-ItemProperty -Path ... -Name ... -Value ..." style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:12,fontFamily:'monospace',boxSizing:'border-box',resize:'vertical'}} />
                  <div style={{fontSize:11,color:'#64748b',marginTop:4}}>Windows: PowerShell · Linux: Bash · Runs as SYSTEM/root with full privileges</div>
                </div>
              )}

              <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:20}}>
                <button type="button" onClick={()=>setShowForm(false)} style={{padding:'10px 20px',border:'1px solid #d1d5db',borderRadius:8,background:'#fff',cursor:'pointer',fontWeight:600}}>Cancel</button>
                <button type="submit" disabled={saving} style={{padding:'10px 20px',background:saving?'#94a3b8':'#1e40af',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:saving?'default':'pointer'}}>
                  {saving?'Saving...':editing?'Update Profile':'Create Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:32,width:460,maxWidth:'95vw'}}>
            <h3 style={{margin:'0 0 4px',fontSize:18,fontWeight:700}}>Assign: {assignModal.name}</h3>
            <p style={{margin:'0 0 16px',color:'#64748b',fontSize:13}}>Select devices to apply this policy profile</p>
            <div style={{display:'flex',gap:8,marginBottom:8}}>
              <button onClick={()=>setSelDevs(devices.map(d=>d.id))} style={{fontSize:11,padding:'4px 10px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer'}}>All Devices</button>
              <button onClick={()=>setSelDevs(devices.filter(d=>d.status==='online').map(d=>d.id))} style={{fontSize:11,padding:'4px 10px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer'}}>All Online</button>
              <button onClick={()=>setSelDevs([])} style={{fontSize:11,padding:'4px 10px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer'}}>Clear</button>
            </div>
            <div style={{maxHeight:220,overflow:'auto',border:'1px solid #e2e8f0',borderRadius:8,marginBottom:16}}>
              {devices.map(d=>(
                <label key={d.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #f8fafc',cursor:'pointer',background:selDevs.includes(d.id)?'#eff6ff':'#fff'}}>
                  <input type="checkbox" checked={selDevs.includes(d.id)} onChange={e=>setSelDevs(s=>e.target.checked?[...s,d.id]:s.filter(x=>x!==d.id))} />
                  <span style={{flex:1,fontSize:13,fontWeight:500}}>{d.hostname}</span>
                  <span style={{fontSize:11,color:d.status==='online'?'#16a34a':'#94a3b8',fontWeight:600}}>{d.status==='online'?'●':'○'} {d.status}</span>
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setAssignModal(null)} style={{padding:'10px 20px',border:'1px solid #d1d5db',borderRadius:8,background:'#fff',cursor:'pointer',fontWeight:600}}>Cancel</button>
              <button onClick={assign} disabled={assigning||!selDevs.length} style={{padding:'10px 20px',background:assigning||!selDevs.length?'#94a3b8':'#1e40af',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:assigning?'default':'pointer'}}>
                {assigning ? 'Assigning...' : `Assign to ${selDevs.length} device(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
